import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/auth/authStore';
import { ErrorBox, Loading, PageTitle } from '@/components/ui';

type RewardKind = 'gems' | 'coins';

interface IapProduct {
  id: string;
  productId: string;
  name: string;
  description: string | null;
  rewardKind: RewardKind;
  rewardAmount: number;
  bonusAmount: number;
  priceUsd: number | null;
  badge: string | null;
  imageUrl: string | null;
  isActive: boolean;
  sortOrder: number;
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;
  const headers: Record<string, string> = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  headers['Content-Type'] = 'application/json';
  const res = await fetch(path, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (!res.ok) {
    let d = '';
    try { const j = await res.json(); d = j?.message ?? j?.error ?? ''; } catch { /* */ }
    throw new Error(d || `HTTP ${res.status}`);
  }
  return res.json();
}

async function uploadImage(file: File): Promise<string> {
  const token = useAuthStore.getState().token;
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/admin/uploads/cosmetic', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  const { url } = await res.json();
  return url as string;
}

export function IapPage() {
  const [items, setItems] = useState<IapProduct[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const load = () =>
    req<{ items: IapProduct[] }>('GET', '/admin/iap/')
      .then(({ items }) => setItems(items))
      .catch((e) => setErr(String(e)));

  useEffect(() => { void load(); }, []);

  if (err) return <ErrorBox text={err} />;
  if (!items) return <Loading />;

  async function createNew() {
    setCreating(true);
    try {
      const suffix = Math.random().toString(36).slice(2, 6);
      await req('POST', '/admin/iap/', {
        productId: `gems_pack_${suffix}`,
        name: 'New Pack',
        rewardKind: 'gems',
        rewardAmount: 100,
        sortOrder: items!.length,
      });
      await load();
    } catch (e: any) { alert(e?.message); }
    setCreating(false);
  }

  return (
    <div>
      <PageTitle title="IAP Products" />
      <div className="card" style={{ padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--ink-muted)', background: 'rgba(34,211,238,0.08)', borderColor: 'rgba(34,211,238,0.25)' }}>
        Real-money packs sold via the native stores. The <b>real price</b> is set in App Store Connect
        and Google Play Console — not here. Each <b>Product ID</b> must match the SKU created in BOTH
        consoles. This page maps a Product ID to the in-game reward the server grants once a purchase
        receipt is verified. <b>Price (USD)</b> is display-only reference text.
      </div>
      <button className="btn btn-primary" onClick={createNew} disabled={creating} style={{ marginBottom: 16 }}>
        + NEW PRODUCT
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {items.map((p) => <IapRow key={p.id} item={p} onChanged={load} />)}
        {items.length === 0 && (
          <div style={{ color: 'var(--ink-dim)', fontSize: 12, padding: 12 }}>No products yet.</div>
        )}
      </div>
    </div>
  );
}

function IapRow({ item, onChanged }: { item: IapProduct; onChanged: () => void }) {
  const [productId, setProductId] = useState(item.productId);
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? '');
  const [rewardKind, setRewardKind] = useState<RewardKind>(item.rewardKind);
  const [rewardAmount, setRewardAmount] = useState(item.rewardAmount);
  const [bonusAmount, setBonusAmount] = useState(item.bonusAmount);
  const [priceUsd, setPriceUsd] = useState(item.priceUsd ?? 0);
  const [badge, setBadge] = useState(item.badge ?? '');
  const [imageUrl, setImageUrl] = useState(item.imageUrl ?? '');
  const [isActive, setIsActive] = useState(item.isActive);
  const [sortOrder, setSortOrder] = useState(item.sortOrder);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleImageUpload(file: File) {
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImageUrl(url);
    } catch (e: any) { alert(e?.message); }
    setUploading(false);
  }

  async function save() {
    setBusy(true);
    try {
      await req('PATCH', `/admin/iap/${item.id}`, {
        productId, name, description: description || null, rewardKind,
        rewardAmount, bonusAmount, priceUsd: priceUsd || null, badge: badge || null,
        imageUrl: imageUrl || null, isActive, sortOrder,
      });
      await onChanged();
    } catch (e: any) { alert(e?.message); }
    setBusy(false);
  }
  async function del() {
    if (!confirm(`Delete product "${name}"?`)) return;
    setBusy(true);
    try { await req('DELETE', `/admin/iap/${item.id}`); await onChanged(); }
    catch (e: any) { alert(e?.message); }
    setBusy(false);
  }

  const total = rewardAmount + bonusAmount;

  return (
    <div className="card" style={{ padding: 16, opacity: isActive ? 1 : 0.6 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Product ID (store SKU)</label>
          <input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="gems_500" style={{ fontFamily: 'var(--f-mono)' }} />
          <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} />
          <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Description</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Badge</label>
              <input value={badge} onChange={(e) => setBadge(e.target.value)} placeholder="BEST VALUE" style={{ width: '100%' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Sort order</label>
              <input type="number" value={sortOrder} onChange={(e) => setSortOrder(+e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>

          {/* Image upload */}
          <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Pack image</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div
              style={{
                width: 72, height: 72, borderRadius: 10, border: '1px solid var(--hairline)',
                background: 'rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center',
                justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
              }}
            >
              {imageUrl
                ? <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                : <span style={{ fontSize: 24 }}>💎</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
              <button
                className="btn"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                style={{ fontSize: 10, padding: '4px 10px' }}
              >
                {uploading ? 'UPLOADING…' : imageUrl ? 'REPLACE' : 'UPLOAD'}
              </button>
              {imageUrl && (
                <button
                  className="btn"
                  onClick={() => setImageUrl('')}
                  style={{ fontSize: 10, padding: '4px 10px', color: 'var(--c-red)' }}
                >
                  REMOVE
                </button>
              )}
            </div>
          </div>
          <input
            type="file"
            accept="image/png,image/webp,image/jpeg,image/gif"
            ref={fileRef}
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleImageUpload(f); e.target.value = ''; }}
          />
        </div>

        <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Reward</label>
              <select value={rewardKind} onChange={(e) => setRewardKind(e.target.value as RewardKind)} style={{ width: '100%' }}>
                <option value="gems">gems</option>
                <option value="coins">coins</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Price (USD, ref)</label>
              <input type="number" step="0.01" value={priceUsd} onChange={(e) => setPriceUsd(+e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Base amount</label>
              <input type="number" value={rewardAmount} onChange={(e) => setRewardAmount(+e.target.value)} style={{ width: '100%' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, color: 'var(--ink-muted)' }}>Bonus</label>
              <input type="number" value={bonusAmount} onChange={(e) => setBonusAmount(+e.target.value)} style={{ width: '100%' }} />
            </div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--c-cyan)', fontFamily: 'var(--f-mono)' }}>
            Grants {total} {rewardKind}{bonusAmount > 0 ? ` (${rewardAmount} + ${bonusAmount})` : ''}
          </div>
          <label style={{ fontSize: 12 }}>
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> active (shown in shop)
          </label>
          <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
            <button className="btn btn-primary" onClick={save} disabled={busy}>SAVE</button>
            <button className="btn" onClick={del} disabled={busy} style={{ color: 'var(--c-red)' }}>DELETE</button>
          </div>
        </div>
      </div>
    </div>
  );
}
